/**
 * A standing marker on any deployment that is not production.
 *
 * It exists so nobody mistakes staging for the real thing — enters a real
 * customer, trusts a number, or reports a bug against data that was never
 * real. It is deliberately hard to miss and deliberately cheap: one
 * unauthenticated request at start-up, no dependency on the session, and
 * nothing rendered at all in production.
 *
 * The build identifier rides along because "is my change deployed?" is the
 * question a promotion actually needs answered, and answering it from the
 * page beats reading a dashboard.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';

import { useLanguage } from '../i18n/language.js';

interface Deployment {
  readonly environment: string;
  readonly service_version: string;
  readonly release: string | null;
}

/** Read by fixed chrome as var(--mj-environment-banner-height, 0px). */
const BANNER_HEIGHT_PROPERTY = '--mj-environment-banner-height';

const COPY = {
  en: {
    label: 'Staging',
    body: 'Test environment. Nothing here is real, and everything may be wiped.',
    build: 'build',
  },
  ar: {
    label: 'بيئة اختبار',
    body: 'بيئة اختبار. لا شيء هنا حقيقي، وقد يُمسح كل شيء.',
    build: 'الإصدار',
  },
} as const;

/** Something that at least behaves like a successful `Response`. */
function isSuccessfulResponse(value: unknown): value is Response {
  return (
    typeof value === 'object' &&
    value !== null &&
    'ok' in value &&
    Boolean((value as { ok?: unknown }).ok) &&
    typeof (value as { json?: unknown }).json === 'function'
  );
}

export function EnvironmentBanner(): ReactNode {
  const { language } = useLanguage();
  const [deployment, setDeployment] = useState<Deployment | null>(null);

  useEffect(() => {
    // A holder rather than a bare `let`: the flag is written in the cleanup
    // and read after an await, which the compiler's flow analysis cannot
    // follow — it narrows the bare form to a constant and the read becomes
    // "unnecessary". A property read is honest about being mutable.
    const live = { cancelled: false };
    // Everything is inside the try, including the call itself: a deployment
    // that cannot answer — or a test whose stub answers nothing at all —
    // must leave the page exactly as it was. Treating silence as production
    // is the safe default: a staging banner on the real site would be worse
    // than no banner on staging.
    void (async () => {
      try {
        // Read through an `unknown`-returning view of fetch. The declared
        // type promises a Response; a stubbed global need not keep that
        // promise, and this component must survive one that does not.
        const call = fetch as (input: string, init?: RequestInit) => Promise<unknown>;
        const response = await call('/environment', { credentials: 'same-origin' });
        if (!isSuccessfulResponse(response)) return;
        const body = (await response.json()) as Deployment;
        if (!live.cancelled) setDeployment(body);
      } catch {
        /* silence means production */
      }
    })();
    return () => {
      live.cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (deployment === null || deployment.environment === 'production') return;
    // The header is authoritative for crawlers; this is for anything reading
    // the document, and for a person who views source.
    const existing = document.querySelector('meta[name="robots"]');
    if (existing !== null) existing.setAttribute('content', 'noindex, nofollow');
  }, [deployment]);

  const banner = useRef<HTMLDivElement | null>(null);

  // Fixed chrome elsewhere (the merchant sidebar) needs to know how tall this
  // strip is, since sticky positioning does not push a fixed element down.
  // Published as one custom property; 0 whenever the banner is absent.
  useEffect(() => {
    const root = document.documentElement;
    const element = banner.current;
    if (element === null) {
      root.style.setProperty(BANNER_HEIGHT_PROPERTY, '0px');
      return undefined;
    }
    const publish = () => {
      root.style.setProperty(
        BANNER_HEIGHT_PROPERTY,
        `${String(Math.ceil(element.getBoundingClientRect().height))}px`,
      );
    };
    publish();
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    observer?.observe(element);
    return () => {
      observer?.disconnect();
      root.style.setProperty(BANNER_HEIGHT_PROPERTY, '0px');
    };
  }, [deployment]);

  if (deployment === null || deployment.environment === 'production') return null;
  const copy = COPY[language];

  return (
    <div
      ref={banner}
      className="mj-environment-banner"
      role="status"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        flexWrap: 'wrap',
        padding: '6px 16px',
        background: 'var(--status-warning-bg)',
        color: 'var(--status-warning-fg)',
        borderBlockEnd: '1px solid var(--status-warning-fg)',
        font: 'var(--type-caption)',
        textAlign: 'center',
      }}
    >
      <strong style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {copy.label}
      </strong>
      <span>{copy.body}</span>
      <span className="mj-ltr" dir="ltr" style={{ opacity: 0.85 }}>
        {copy.build} {deployment.release ?? deployment.service_version}
      </span>
    </div>
  );
}
